// Shared numeric/format/mode helpers for the OC module.
// only2dec / formatEUR are replicated IDENTICALLY from the OCs-builder original.

import {
  OcApiMode,
  OcInternalMode,
  OcItem,
  OcItemMedios,
  OcItemCreatividades,
  OcItemProveedores,
  OcItemProveedoresIncluye,
  OcTotalsMedios,
  OcTotalsCreatividades,
} from "../../types/oc";

export function only2dec(n: unknown): number {
  const v = Number(n);
  if (Number.isNaN(v)) return 0;
  return Math.round(v * 100) / 100;
}

// Coerce a possibly-empty/string field to a number. The backend rejects "" for
// numeric fields (422 "invalid float number"), so empties become 0.
function toNum(v: unknown): number {
  return Number(v) || 0;
}

// Normalize the numeric fields of each OC line for the given mode so the POST
// payload never contains empty strings where the contract expects a number.
export function normalizeOcItems(
  mode: OcInternalMode,
  items: OcItem[]
): OcItem[] {
  switch (mode) {
    case "oc-medios":
      // Pick only the contract fields — this also strips the internal fee-line
      // marker (FEE_LINE_FLAG) so it never reaches the backend.
      return (items as OcItemMedios[]).map((it) => ({
        concepto: it.concepto,
        inversion: toNum(it.inversion),
        fee: toNum(it.fee),
      }));
    case "oc-creatividades":
      return (items as OcItemCreatividades[]).map((it) => ({
        ...it,
        cantidad: toNum(it.cantidad),
        precio: toNum(it.precio),
      }));
    case "oc-proveedores":
      return (items as OcItemProveedores[]).map((it) => ({
        ...it,
        inv: toNum(it.inv),
        costKPI: toNum(it.costKPI),
      }));
    case "oc-proveedores-incluye":
      return (items as OcItemProveedoresIncluye[]).map((it) => ({
        ...it,
        coste: toNum(it.coste),
      }));
    default:
      return items;
  }
}

export function formatEUR(value: unknown): string {
  const num = Number(value || 0);
  return num.toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
}

// Format a "YYYY-MM-DD" string directly to "DD/MM/YYYY" WITHOUT going through
// new Date(), which would shift the day across timezones.
export function formatDateES(isoDate?: string): string {
  if (!isoDate) return "";
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(isoDate);
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

// ---- Mode mapping (API enum <-> internal string) ---------------------------
const API_TO_INTERNAL: Record<OcApiMode, OcInternalMode> = {
  OC_MEDIOS: "oc-medios",
  OC_CREATIVIDADES: "oc-creatividades",
  OC_PROVEEDORES: "oc-proveedores",
  OC_PROVEEDORES_INCLUYE: "oc-proveedores-incluye",
};

const INTERNAL_TO_API: Record<OcInternalMode, OcApiMode> = {
  "oc-medios": "OC_MEDIOS",
  "oc-creatividades": "OC_CREATIVIDADES",
  "oc-proveedores": "OC_PROVEEDORES",
  "oc-proveedores-incluye": "OC_PROVEEDORES_INCLUYE",
};

export function apiToInternalMode(mode: OcApiMode): OcInternalMode {
  return API_TO_INTERNAL[mode];
}

export function internalToApiMode(mode: OcInternalMode): OcApiMode {
  return INTERNAL_TO_API[mode];
}

// ---- Totals per mode (identical to the original) ---------------------------
export function totalsMedios(items: OcItemMedios[]): OcTotalsMedios {
  const inv = items.reduce((acc, it) => acc + only2dec(it.inversion), 0);
  const fee = items.reduce((acc, it) => acc + only2dec(it.fee), 0);
  const tot = inv + fee;
  return { inv: only2dec(inv), fee: only2dec(fee), tot: only2dec(tot) };
}

// Marker for the auto-generated fee line so it can be located and replaced on
// re-apply. Stripped by normalizeOcItems before the POST payload is built.
export const FEE_LINE_FLAG = "__feeLine";

// Rebuild the media items with a single dedicated fee line that holds the whole
// fee. Existing media lines keep their concepto/inversion but their fee is
// reset to 0, and any previous fee line is removed and replaced. The fee line
// carries 0 investment, so totals.inv is unaffected and totals.fee === feeTotal
// (totalsMedios stays the single source of truth). Returns the original items
// unchanged when there are no media lines to attach a fee to.
export function setFeeLine(
  items: OcItemMedios[],
  feeTotal: number,
  label: string
): OcItemMedios[] {
  const media = items
    .filter((it) => !(it as unknown as Record<string, unknown>)[FEE_LINE_FLAG])
    .map((it) => ({ ...it, fee: 0 }));
  if (media.length === 0) return items;

  const feeLine = {
    concepto: label,
    inversion: 0,
    fee: only2dec(feeTotal),
    [FEE_LINE_FLAG]: true,
  } as OcItemMedios;
  return [...media, feeLine];
}

export function totalsCreatividades(
  items: OcItemCreatividades[]
): OcTotalsCreatividades {
  const { cant, tot } = items.reduce(
    (acc, it) => {
      const c = only2dec(it.cantidad);
      const p = only2dec(it.precio);
      acc.cant += c;
      acc.tot += c * p;
      return acc;
    },
    { cant: 0, tot: 0 }
  );
  return { cant: only2dec(cant), tot: only2dec(tot) };
}

// Default total for proveedores = sum of investments (then editable manually).
export function totalsProveedoresDefault(items: OcItemProveedores[]): number {
  return only2dec(items.reduce((acc, it) => acc + only2dec(it.inv), 0));
}

export function totalsProveedoresIncluye(
  items: OcItemProveedoresIncluye[]
): number {
  return only2dec(items.reduce((acc, it) => acc + only2dec(it.coste), 0));
}

// Human label for a mode (Spanish), used in the history table / filters.
export function ocModeLabel(mode: OcApiMode): string {
  switch (mode) {
    case "OC_MEDIOS":
      return "Medios";
    case "OC_CREATIVIDADES":
      return "Creatividades";
    case "OC_PROVEEDORES":
      return "Proveedores";
    case "OC_PROVEEDORES_INCLUYE":
      return "Proveedores (incluye)";
    default:
      return mode;
  }
}
